import React, {
  useState,
  useEffect,
  forwardRef,
  useId,
  useContext,
} from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Typography } from '../typography/Typography';
import { Stack } from '../stack/Stack';
import { useBackgroundColor } from '../../utils';
import { SectionContext } from '../../context/SectionContext';
import { Expand } from '../icon/icons/Expand';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import tokens from './tokens';

// Interface

export interface ExpanderPanelProps {
  title: string;
  subTitle?: string;
  as?: 'div' | 'section' | 'li';
  open?: boolean;
  borderBottomOnly?: boolean;
  disabled?: boolean;
  ref?: any;
  content?: any;
  children: React.ReactNode;
  background: ThemeColorsKeys;
}

export interface ButtonProps {
  url?: string;
  label?: string;
  onClick?: any;
}

interface StyledExpanderPanelProps {
  isOpen?: boolean;
  background?: ThemeColorsKeys;
  borderBottomOnly?: boolean;
}

interface StyledHeadingButtonProps {
  borderBottomOnly?: boolean;
}

interface StyledExpanderContentProps {
  borderBottomOnly?: boolean;
  background: ThemeColorsKeys;
}

// Styles

const StyledExpanderPanel = styled.div<StyledExpanderPanelProps>`
  ${({
    theme: { colors, borderRadii },
    isOpen,
    background,
    borderBottomOnly,
  }) => css`
    border-radius: ${borderBottomOnly ? 0 : borderRadii[tokens.borderRadius]};
    ${borderBottomOnly
      ? `border-bottom: 1px solid ${
          isOpen ? colors[tokens.borderColor] : colors[tokens.borderColorOpen]
        };`
      : `border: 1px solid ${
          isOpen ? colors[tokens.borderColor] : colors[tokens.borderColorOpen]
        };`}
    border-top: 0;
    ${!borderBottomOnly &&
    useBackgroundColor(tokens.headerBackgroundColor[background])}

    &:hover {
      border-color: ${colors[tokens.hoverBackgroundColour]};
      ${!borderBottomOnly &&
      useBackgroundColor(tokens.headerBackgroundColorHover[background])}
      ${borderBottomOnly && `& h2 {color: ${colors.brandOne80};}`}

      h3 {
        text-decoration: ${tokens.hoverTextDecoration};
      }
    }

    &:last-of-type {
      ${borderBottomOnly && 'border-bottom: 0;'}

      ${borderBottomOnly &&
      `${StyledExpanderContent} {
        padding-bottom: 0;
      }`}
    }

    /* Animate content opening */
    ${StyledExpanderContent} {
      display: ${isOpen ? 'block' : 'none'};
    }
  `};
`;

const StyledExpanderContent = styled('div', {
  target: 'content',
})<StyledExpanderContentProps>`
  ${({ theme: { space, borderRadii }, borderBottomOnly, background }) => css`
    ${borderBottomOnly
      ? `padding: 0 0 ${space.fluidMedium} 0;`
      : `padding: ${space.fluidMedium};`}
    ol {
      list-style-type: decimal;
    }

    ${!borderBottomOnly &&
    useBackgroundColor(tokens.contentBackgroundColor[background])}

    border-radius: ${borderRadii[tokens.borderRadius]};
  `};
`;

const StyledExpand = styled(Expand)<StyledExpanderPanelProps>`
  ${({ isOpen }) => css`
    transition: transform 0.1s ease-in-out;
    ${isOpen &&
    css`
      transform: rotate(180deg);
    `}
  `};
`;

const StyledHeadingButton = styled.button<StyledHeadingButtonProps>`
  ${({ theme: { space }, borderBottomOnly }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    gap: ${space.small};

    h2 {
      padding-top: ${space.xxxSmall};
    }
    ${borderBottomOnly
      ? `padding: 0 0 ${space.medium} 0;`
      : `padding: ${space.small} ${space.fluidMedium};`}
  `};
`;

// JSX

export const ExpanderPanel = forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<ExpanderPanelProps>
>(
  (
    {
      title,
      subTitle,
      as,
      open,
      disabled,
      children,
      background,
      borderBottomOnly,
      ...rest
    },
    ref
  ) => {
    const { dataLayerPush } = useDataLayerPush();
    const [isOpen, setIsOpen] = useState(open);
    const panelId = `expander-panel-${useId()}`;
    const buttonId = `expander-button-${useId()}`;

    useEffect(() => {
      setIsOpen(open);
    }, [open]);

    const handleClick = () => {
      if (disabled) {
        return;
      }

      // Toggle the isOpen state
      const newIsOpen = !isOpen;
      setIsOpen(!isOpen);

      // Execute dataLayerPush
      dataLayerPush({
        content_group: 'brochureware pages',
        event: 'click accordion',
        accordion_class: newIsOpen ? 'accordion opened' : 'accordion closed', // Set the class based on the new state
        accordion_text: title,
      });
    };

    const { sectionBackground } = useContext(SectionContext);

    return (
      <StyledExpanderPanel
        {...rest}
        as={as}
        isOpen={isOpen}
        ref={ref}
        background={
          sectionBackground === 'utilityWhite' ? 'onWhite' : 'onBackground'
        }
        borderBottomOnly={borderBottomOnly}
      >
        <StyledHeadingButton
          type="button"
          onClick={handleClick}
          id={buttonId}
          aria-controls={panelId}
          aria-expanded={isOpen}
          borderBottomOnly={borderBottomOnly}
        >
          <div>
            <Typography component="h3" variant="headingSmall">
              <span>{title}</span>
            </Typography>
            {subTitle && <Typography component="p">{subTitle}</Typography>}
          </div>
          <StyledExpand isOpen={isOpen} />
        </StyledHeadingButton>
        <StyledExpanderContent
          id={panelId}
          tabIndex={isOpen ? 0 : -1}
          aria-labelledby={buttonId}
          aria-hidden={!isOpen}
          borderBottomOnly={borderBottomOnly}
          background={
            sectionBackground === 'utilityWhite' ? 'onWhite' : 'onBackground'
          }
        >
          <Stack>{children}</Stack>
        </StyledExpanderContent>
      </StyledExpanderPanel>
    );
  }
);

ExpanderPanel.displayName = 'ExpanderPanel';
export default ExpanderPanel;
